import { ShieldCheck } from "lucide-react";
import { useBrand } from "../context/BrandContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Bloc de marque réutilisable (logo + nom + sous-titre).
 * Lit dynamiquement la configuration depuis BrandContext.
 * Variants : 'sm' (sidebar), 'md' (login/forgot/reset), 'lg' (app agent header).
 */
export function BrandBlock({ size = "md", invert = false }) {
  const { name, tagline, logo_file_id } = useBrand();

  // Variante hero : logo grand (~10×10 cm = 380px) centré au-dessus du nom
  if (size === "hero") {
    return (
      <div className="flex flex-col items-center text-center gap-5" data-testid="brand-block">
        <div
          className={`rounded-2xl grid place-items-center overflow-hidden ${logo_file_id ? "" : "bg-gradient-to-br from-sky-500 to-indigo-600 shadow-lg"}`}
          style={{ width: "min(40vmin, 380px)", height: "min(40vmin, 380px)" }}
        >
          {logo_file_id ? (
            <img
              src={`${BACKEND}/api/brand/logo`}
              alt={name}
              className="h-full w-full object-contain"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          ) : (
            <ShieldCheck className="h-1/2 w-1/2 text-white" />
          )}
        </div>
        <div>
          <div className={`font-display text-3xl md:text-4xl font-extrabold tracking-tight leading-tight ${invert ? "text-white" : "text-slate-900"}`} data-testid="brand-name">{name}</div>
          {tagline && (
            <div className={`text-xs uppercase tracking-[0.24em] font-bold mt-2 ${invert ? "text-sky-100/90" : "text-slate-500"}`} data-testid="brand-tagline">{tagline}</div>
          )}
        </div>
      </div>
    );
  }

  // Variante sm : sidebar (logo grand centré au-dessus, nom en couleur de marque)
  if (size === "sm") {
    return (
      <div className="flex flex-col items-center text-center gap-2" data-testid="brand-block">
        <div className={`h-20 w-20 rounded-lg grid place-items-center overflow-hidden ${logo_file_id ? "" : "bg-gradient-to-br from-sky-500 to-indigo-600"}`}>
          {logo_file_id ? (
            <img
              src={`${BACKEND}/api/brand/logo`}
              alt={name}
              className="h-full w-full object-contain"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          ) : (
            <ShieldCheck className="h-10 w-10 text-white" />
          )}
        </div>
        <div>
          <div
            className="font-display text-base font-extrabold tracking-tight leading-tight"
            style={{ color: "var(--brand-primary)" }}
            data-testid="brand-name"
          >{name}</div>
          {tagline && (
            <div className="text-[9px] uppercase tracking-[0.18em] font-bold mt-1 text-slate-500" data-testid="brand-tagline">{tagline}</div>
          )}
        </div>
      </div>
    );
  }

  const sizes = {
    sm: { box: "h-9 w-9", icon: "h-5 w-5", title: "text-lg", subtitle: "text-[10px] tracking-[0.18em]" },
    md: { box: "h-11 w-11", icon: "h-6 w-6", title: "text-2xl", subtitle: "text-[10px] tracking-[0.22em]" },
    lg: { box: "h-14 w-14", icon: "h-7 w-7", title: "text-3xl", subtitle: "text-xs tracking-[0.22em]" },
  };
  const s = sizes[size] || sizes.md;

  const titleClass = invert ? "text-white" : "text-slate-900";
  const subClass = invert ? "text-sky-100/90" : "text-slate-500";

  return (
    <div className="flex items-center gap-3" data-testid="brand-block">
      <div className={`${s.box} rounded-md grid place-items-center overflow-hidden ${logo_file_id ? "" : "bg-gradient-to-br from-sky-500 to-indigo-600"}`}>
        {logo_file_id ? (
          <img
            src={`${BACKEND}/api/brand/logo`}
            alt={name}
            className="h-full w-full object-contain"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        ) : (
          <ShieldCheck className={`${s.icon} text-white`} />
        )}
      </div>
      <div>
        <div className={`font-display ${s.title} font-extrabold tracking-tight leading-none ${titleClass}`} data-testid="brand-name">{name}</div>
        {tagline && (
          <div className={`${s.subtitle} uppercase font-bold mt-1 ${subClass}`} data-testid="brand-tagline">{tagline}</div>
        )}
      </div>
    </div>
  );
}
